<template>
  <UContainer
    :ui="{
      base: 'mx-auto relative',
      padding: 'px-4 sm:px-6 lg:px-8',
      constrained: 'max-w-[1920px]',
    }"
  >
    <div class="dish-page">
      <template v-if="product">
        <SeoBreadcrumbs :items="breadcrumbs" />

        <article class="dish-page__card">
          <div class="dish-page__media">
            <img
              v-if="imageUrl"
              :src="imageUrl"
              :alt="productName"
              class="dish-page__image"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
          </div>

          <div class="dish-page__info">
            <h1 class="dish-page__title">{{ productName }}</h1>

            <p v-if="sizeLabel" class="dish-page__size">{{ sizeLabel }}</p>

            <p v-if="description" class="dish-page__description">{{ description }}</p>

            <dl v-if="nutrition.length > 0" class="dish-page__nutrition">
              <div v-for="row in nutrition" :key="row.label" class="dish-page__nutrition-item">
                <dt>{{ row.label }}</dt>
                <dd>{{ row.value }}</dd>
              </div>
            </dl>

            <!-- Состав и аллергены обязательны для продуктов питания (п. 2 ст. 10
                 ЗоЗПП) и должны быть видны до оформления заказа. Эта же страница —
                 индексируемая карточка блюда, так что состав попадает и в поиск. -->
            <div v-if="composition" class="dish-page__composition">
              <h2 class="dish-page__composition-title">Состав</h2>
              <p>{{ composition }}</p>
            </div>

            <div v-if="allergens" class="dish-page__allergens">
              <h2 class="dish-page__composition-title">Аллергены</h2>
              <p>{{ allergens }}</p>
            </div>

            <div class="dish-page__buy">
              <span v-if="price > 0" class="dish-page__price">
                <span v-if="isPriceFrom" class="dish-page__price-prefix">от</span>
                {{ price }} ₽
              </span>
              <UiButtonMain
                v-if="isAvailable"
                color="red"
                size="xl"
                label="Выбрать и добавить"
                @click="openDish"
              />
              <span v-else class="dish-page__unavailable">Сейчас недоступно</span>
            </div>

            <NuxtLink v-if="categoryHref" :to="categoryHref" class="dish-page__category-link">
              Все блюда раздела «{{ categoryName }}»
            </NuxtLink>
          </div>
        </article>

        <!-- Соседи по разделу: гость из поиска попадает сразу в карточку, и без
             этого блока путь дальше у него только один — «назад». -->
        <ProductCatalogSection
          v-if="siblings.length > 0"
          id="dish-siblings"
          title="Из этого же раздела"
          :slug="categorySlug"
          :items="siblings"
        />
      </template>

      <!-- Блюдо убрали из меню, пока гость шёл по ссылке -->
      <div v-else-if="isReady" class="dish-page__missing">
        <h1 class="text-2xl md:text-3xl font-bold brand-font">Блюдо не найдено</h1>
        <p class="text-gray-600 dark:text-gray-400">
          Возможно, его убрали из меню. Загляните в каталог — там всё, что готовим сейчас.
        </p>
        <UiButtonMain color="red" size="xl" label="Перейти в меню" @click="navigateTo('/')" />
      </div>
    </div>
  </UContainer>
</template>

<script setup lang="ts">
/**
 * Страница блюда: /dish/<название>-<id>.
 *
 * До неё у товара не было адреса вообще: карточка открывалась модалкой поверх
 * главной. Значит, по запросу с названием блюда поиску нечего было показать, а
 * гость не мог отправить другу ссылку на конкретную позицию.
 *
 * Товар ищется в уже загруженном каталоге (плагин institution-ssr), поэтому
 * страница рендерится на сервере без дополнительного запроса к API.
 */
import { computed } from 'vue';
import { useInstitutionStore, useBreadcrumbsJsonLd, graphToJson } from '@entities/institution';
import { useCartStore } from '@entities/order';
import { getCardPrice, getCardDescription, getCardImages, getCardComposition, getCardAllergens } from '@entities/product';
import { useModalStore, modalStateEnum } from '@shared/model';
import { categoryPath, dishPath, dishIdFromSlug, setProductOpenSource, useAnalytics } from '@shared/lib';
import { SeoBreadcrumbs } from '~/widgets/seo-breadcrumbs';

const props = defineProps<{ slug: string }>();

const storeInstitution = useInstitutionStore();
const storeModal = useModalStore();
const cartStore = useCartStore();

const slug = computed(() => props.slug);
const dishId = computed(() => dishIdFromSlug(slug.value));

const isReady = computed(() => Boolean(storeInstitution.institution));

/** Категория и товар ищутся одним проходом: дальше нужны обе. */
const found = computed<{ product: any, category: any } | null>(() => {
  const id = dishId.value;
  if (!id) return null;
  for (const category of storeInstitution.products || []) {
    const product = ((category as any)?.products || []).find((item: any) => Number(item?.id) === id);
    if (product) return { product, category };
  }
  return null;
});

const product = computed(() => found.value?.product ?? null);
const category = computed(() => found.value?.category ?? null);

const productName = computed(() => product.value?.name || product.value?.external_name || '');
const categoryName = computed(() => category.value?.category || '');
const categorySlug = computed(() => category.value?.to || '');
const categoryHref = computed(() => categoryPath(categorySlug.value));

/**
 * Цена — тем же хелпером, что и плитка каталога. Голое `product.price` у групп
 * («Шаурма» с размерами) и у комбо равно нулю: настоящая цена лежит в вариантах,
 * и страница показывала бы «0 ₽» там, где каталог показывает «от 300 ₽».
 */
const cardPrice = computed(() => getCardPrice(product.value));
const price = computed(() => cardPrice.value.value || 0);
/** Цена «от»: у товара есть варианты или это комбо. */
const isPriceFrom = computed(() => cardPrice.value.from);

/** Описание группы лежит у первого варианта — берём тем же хелпером, что и карточка. */
const description = computed(() => getCardDescription(product.value));

const imageUrl = computed(() => {
  const own = getCardImages(product.value)?.[0]?.url || '';
  return own || storeInstitution.institution?.visual?.placeholder_file || '';
});

/**
 * Доступность — теми же правилами, что и на витрине (см. useOrderedProducts):
 * стоп-лист, снятие с продажи и выключенная карточка.
 */
const isAvailable = computed(() => {
  const item = product.value;
  if (!item) return false;
  return item.stop_list !== true && item.is_stopped !== true && item.is_active !== false;
});

const sizeLabel = computed(() => {
  const item = product.value;
  if (!item) return '';
  if (item.weight) return `${item.weight} г`;
  if (item.volume) return `${item.volume} мл`;
  return item.size || '';
});

const nutrition = computed(() => {
  const kbju = product.value?.kbju;
  if (!kbju) return [];
  const rows = [
    { label: 'Калории', value: kbju.calories },
    { label: 'Белки', value: kbju.proteins },
    { label: 'Жиры', value: kbju.fats },
    { label: 'Углеводы', value: kbju.carbs ?? kbju.carbohydrates },
  ];
  return rows
    .filter(row => row.value != null && String(row.value).trim() !== '')
    .map(row => ({ label: row.label, value: String(row.value) }));
});

// С фолбэком на варианты (как description): у группы с размерами собственных
// состава и аллергенов нет — прямое чтение оставляло бы страницу без состава
// у всех товаров с вариантами.
const composition = computed(() => getCardComposition(product.value));
const allergens = computed(() => getCardAllergens(product.value));

/** Другие блюда раздела — без текущего и не больше строки карточек. */
const SIBLINGS_LIMIT = 8;

const siblings = computed(() =>
  ((category.value?.products || []) as any[])
    .filter(item => Number(item?.id) !== dishId.value)
    .slice(0, SIBLINGS_LIMIT),
);

/**
 * Кнопка открывает ту же модалку, что и карточка в каталоге: размеры, добавки и
 * прочая настройка живут там, и второй такой формы на сайте быть не должно.
 */
const openDish = () => {
  if (!product.value) return;
  setProductOpenSource('catalog');
  useAnalytics().track('product_card_click', {
    productId: Number(product.value.id) || 0,
    categoryId: product.value.category_id ?? undefined,
    price: price.value || undefined,
    source: 'catalog',
  });
  cartStore.setSelectedDish(product.value);
  storeModal.open(modalStateEnum.MoreAboutDishRefactored);
};

const institutionName = computed(() => storeInstitution.institution?.name?.trim() || '');
const city = computed(() => (storeInstitution.selectedBranch as any)?.address?.city?.trim() || '');

const pageTitle = computed(() => {
  if (!productName.value) return 'Блюдо не найдено';
  const where = [institutionName.value, city.value].filter(Boolean).join(', ');
  return where ? `${productName.value} — заказать с доставкой | ${where}` : productName.value;
});

/**
 * Описание: состав блюда, если он заполнен, иначе — осмысленный шаблон.
 * Обрезаем по границе слова: рваное слово в сниппете читается как ошибка сайта.
 */
const DESCRIPTION_LIMIT = 160;

const metaDescription = computed(() => {
  if (!product.value) return '';
  const own = description.value.replaceAll(/\s+/g, ' ').trim();
  const where = city.value ? ` в ${city.value}` : '';
  const brand = institutionName.value ? ` — ${institutionName.value}` : '';
  const fromPrefix = isPriceFrom.value ? 'от ' : '';
  const priceTail = price.value > 0 ? ` Цена ${fromPrefix}${price.value} ₽.` : '';
  const fallback = `${productName.value} с доставкой${where}${brand}.${priceTail}`;

  const full = own ? `${own}${priceTail}` : fallback;
  if (full.length <= DESCRIPTION_LIMIT) return full;
  const cut = full.slice(0, DESCRIPTION_LIMIT);
  return `${cut.slice(0, cut.lastIndexOf(' ')) || cut}…`;
});

const breadcrumbs = computed(() => {
  if (!product.value) return [];
  const trail = [{ name: 'Главная', path: '/' }];
  if (categoryName.value && categoryHref.value) {
    trail.push({ name: categoryName.value, path: categoryHref.value });
  }
  trail.push({ name: productName.value, path: dishPath(product.value) });
  return trail;
});

useBreadcrumbsJsonLd(() => breadcrumbs.value);

const reqUrl = useRequestURL({ xForwardedHost: true, xForwardedProto: true });

/** Канонический адрес блюда — с текущим названием, каким бы ни был адрес входа. */
const canonicalPath = computed(() => (product.value ? dishPath(product.value) : ''));

/**
 * Product с offers, а не MenuItem: расширенный сниппет с ценой и наличием
 * Google строит именно по Product. MenuItem он понимает, но в выдаче не
 * показывает — а цена в сниппете для доставки еды и есть половина клика.
 */
const productJsonLd = computed(() => {
  if (!product.value) return '';
  const node: Record<string, unknown> = {
    '@type': 'Product',
    '@id': `${reqUrl.origin}${canonicalPath.value}#product`,
    name: productName.value,
    url: `${reqUrl.origin}${canonicalPath.value}`,
    category: categoryName.value || undefined,
  };
  if (description.value) node.description = description.value;
  const image = getCardImages(product.value)?.[0]?.url;
  if (image) node.image = image;
  if (institutionName.value) node.brand = { '@type': 'Brand', name: institutionName.value };
  if (price.value > 0) {
    const availability = isAvailable.value
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock';
    const seller = { '@id': `${reqUrl.origin}/#restaurant` };
    const url = `${reqUrl.origin}${canonicalPath.value}`;

    // Цена «от» — это AggregateOffer с lowPrice, а не Offer с price: у группы
    // размеров и у комбо одной цены не существует, и указывать минимальную как
    // точную значит обещать в выдаче сумму, которой в корзине не будет.
    node.offers = isPriceFrom.value
      ? {
          '@type': 'AggregateOffer',
          lowPrice: String(price.value),
          priceCurrency: 'RUB',
          url,
          availability,
          offerCount: Array.isArray(product.value.variants) ? product.value.variants.length : 1,
          seller,
        }
      : {
          '@type': 'Offer',
          price: String(price.value),
          priceCurrency: 'RUB',
          url,
          availability,
          seller,
        };
  }
  return graphToJson([node]);
});

/**
 * Блюда нет в меню — 404, а не 200 с текстом «не найдено»: soft 404 поисковик
 * держит в индексе как рабочую страницу.
 */
if (import.meta.server) {
  const event = useRequestEvent();
  if (event && isReady.value && !product.value) setResponseStatus(event, 404);
}

useSeoMeta({
  title: () => pageTitle.value,
  description: () => metaDescription.value,
  ogTitle: () => pageTitle.value,
  ogDescription: () => metaDescription.value,
});

useHead(() => ({
  meta: product.value ? [] : [{ name: 'robots', content: 'noindex, follow' }],
  script: productJsonLd.value
    ? [{ type: 'application/ld+json', innerHTML: productJsonLd.value, key: 'ld-json-product' }]
    : [],
}));
</script>

<style scoped lang="scss">
.dish-page {
  &__card {
    @apply grid gap-6 md:gap-10 md:grid-cols-2 pt-6 md:pt-10;
  }

  &__media {
    @apply relative w-full overflow-hidden;
    aspect-ratio: 1 / 1;
    border-radius: var(--app-radius-media, 16px);
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.05));
  }

  &__image {
    @apply w-full h-full object-cover object-center;
  }

  &__info {
    @apply flex flex-col items-start gap-4;
  }

  &__title {
    @apply text-2xl md:text-4xl font-bold tracking-tight;
    font-family: var(--app-brand-font, inherit);
  }

  &__size {
    @apply text-sm text-gray-500 dark:text-gray-400;
  }

  &__description {
    @apply text-base text-gray-700 dark:text-gray-300;
  }

  &__nutrition {
    @apply grid grid-cols-2 sm:grid-cols-4 gap-3 w-full;
  }

  &__nutrition-item {
    @apply flex flex-col gap-1 p-3 bg-gray-100 dark:bg-zinc-800;
    border-radius: var(--app-radius-control, 12px);

    dt {
      @apply text-xs text-gray-500 dark:text-gray-400;
    }

    dd {
      @apply text-base font-semibold text-gray-900 dark:text-white;
    }
  }

  &__composition {
    @apply w-full text-sm leading-relaxed text-gray-700 dark:text-gray-300;
  }

  &__composition-title {
    @apply text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1;
  }

  // Аллергены — отдельным заметным блоком: их читают, чтобы решить, можно ли есть.
  &__allergens {
    @apply w-full text-sm leading-relaxed p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900;
    border-radius: var(--app-radius-control, 12px);
  }

  &__buy {
    @apply flex flex-wrap items-center gap-4 pt-2;
  }

  &__price {
    @apply inline-flex items-baseline gap-1 text-2xl md:text-3xl font-bold text-gray-900 dark:text-white;
    font-variant-numeric: tabular-nums;
  }

  &__price-prefix {
    @apply text-sm font-medium opacity-60;
  }

  &__unavailable {
    @apply text-base font-semibold text-gray-500 dark:text-gray-400;
  }

  &__category-link {
    @apply text-sm font-semibold no-underline;
    @apply text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white;
  }

  &__missing {
    @apply max-w-3xl mx-auto py-16 flex flex-col items-start gap-4;
  }
}
</style>
